import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import PageContainer from "../pageContainer";
import { useInView } from "react-intersection-observer";
import { when } from "../../styles/theme";
import { GatsbyImage } from "gatsby-plugin-image";
import { SectionTitle } from "../typography.js";

export const fragment = graphql`
  fragment TimelineContent on ContentfulPageSectionTimeline {
    name
    title
    timelineItems {
      title
      name
      year
      description {
        childMarkdownRemark {
          html
        }
      }
      image {
        gatsbyImageData(layout: CONSTRAINED, width: 640)
        description
      }
    }
    internal {
      type
    }
  }
`;

const TimelineSection = styled(PageContainer)`
  margin-top: 10rem;
`;

const TimelineItemsContainer = styled.div`
  margin-top: 5rem;
  display: ${(props) => (props.isRight ? "none" : "block")};

  ${when("md")} {
    display: block;
    ${(props) => (props.isRight ? `width: calc(50% - 2rem)` : `flex: 1;`)}
  }

  ${when("xxl")} {
    ${(props) => (props.isRight ? `width: calc(50% - 3rem)` : `flex: 1;`)}
  }
`;

const Square = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: ${(props) => props.theme.colors.red};
  right: 2rem;
  top: ${(props) => (props.isFirst ? "0" : "50%")};
  transform: translate(50%, -50%) rotate(45deg);

  &:before {
    content: "";
    position: absolute;
    border: 1px solid ${(props) => props.theme.colors.red};
    left: -3px;
    right: -3px;
    bottom: -3px;
    top: -3px;
  }

  ${when("sm")} {
    right: 7rem;
  }

  ${when("md")} {
    right: -2rem;
  }
  ${when("xxl")} {
    right: -3rem;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-right: ${(props) => props.theme.pageContainerWidth.xs + 4}rem;
  margin-top: 5rem;

  ${when("sm")} {
    padding-right: ${(props) => props.theme.pageContainerWidth.sm + 14}rem;
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    right: 2rem;
    bottom: 0;
    width: 1px;
    background: ${(props) => props.theme.colors.red};

    ${when("sm")} {
      right: 7rem;
    }

    ${when("md")} {
      right: 2rem;
    }

    ${when("xxl")} {
      right: 3rem;
    }
  }

  &:before {
    content: "";
    position: absolute;
    right: 2rem;
    width: 1px;
    height: 5rem;
    bottom: 100%;
    background: ${(props) => props.theme.colors.red};

    ${when("sm")} {
      right: 7rem;
    }

    ${when("md")} {
      right: 2rem;
    }

    ${when("xxl")} {
      right: 3rem;
    }
  }

  &:first-child {
    margin-top: 0;

    &:before {
      display: none;
    }
  }

  &:last-child {
    &:after {
      bottom: 50%;
    }
  }

  ${when("md")} {
    padding-right: 4rem;
    &:nth-child(odd) {
      ${(props) => props.isRight && `visibility: hidden;`}
    }

    &:nth-child(odd) {
      &:not:first-child {
        ${(props) => props.isRight && `height: 50%;`}
      }
    }

    &:nth-child(even) {
      height: 0;
      margin-top: 0;
      ${(props) => props.isRight && `padding-right: 0;`}

      &:before {
        right: unset;
        left: calc(-2rem - 1px);
      }

      &:after {
        display: none;
      }

      ${Square} {
        right: unset;
        left: -3rem;
      }

      ${(props) =>
        props.isRight
          ? `transform: translateY(-15rem);`
          : `visibility: hidden;`}
    }
  }

  ${when("xxl")} {
    padding-right: 6rem;

    &:nth-child(even) {
      &:before {
        left: calc(-3rem - 1px);
      }

      ${Square} {
        left: -4rem;
      }
    }
  }
`;

const TimelineContent = styled.div`
  background-color: ${(props) => props.theme.colors.red};
  border-radius: 1rem;
  padding: 2rem;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isRight ? "flex-start" : "flex-end")};

  ${when("md")} {
    position: relative;
  }
`;

const ImageWrapper = styled.div``;

const Year = styled.div`
  font-size: 2.4rem;
  margin-top: 1.5rem;
  font-weight: 600;
  line-height: 1;
`;

const ItemTitle = styled.div`
  font-size: 2rem;
  font-weight: 500;
  margin-top: 0.5rem;
  line-height: 1;
`;

const Description = styled.div`
  font-size: 1.4rem;
  font-weight: 400;
  text-align: ${(props) => (props.isRight ? "left" : "right")};
  margin-top: 1.5rem;
  max-width: 60rem;
`;

const TimelineSetContainer = styled.div`
  ${when("md")} {
    display: flex;
  }
`;

const TimelineSet = ({ timelineItems, isRight }) => {
  return (
    <TimelineItemsContainer isRight={isRight}>
      {timelineItems.map((item, index) => (
        <TimelineItem key={index} isRight={isRight}>
          <TimelineContent isRight={isRight}>
            {item.image && (
              <ImageWrapper>
                <GatsbyImage
                  image={item.image.gatsbyImageData}
                  alt={item.image.description}
                />
              </ImageWrapper>
            )}
            <Year>{item.year}</Year>
            <ItemTitle>{item.title}</ItemTitle>
            <Description
              isRight={isRight}
              dangerouslySetInnerHTML={{
                __html: item.description.childMarkdownRemark.html,
              }}
            ></Description>
            <Square isFirst={index === 0} />
          </TimelineContent>
        </TimelineItem>
      ))}
    </TimelineItemsContainer>
  );
};

const Timeline = ({ data, setCurrentSection, index }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const { title, timelineItems } = data;

  return (
    <TimelineSection
      hasSideBar={true}
      as={"section"}
      ref={ref}
      id={`section-${index}`}
    >
      <SectionTitle>{title}</SectionTitle>

      <TimelineSetContainer>
        <TimelineSet timelineItems={timelineItems} />
        <TimelineSet timelineItems={timelineItems} isRight={true} />
      </TimelineSetContainer>
    </TimelineSection>
  );
};
export const timelineName = "ContentfulPageSectionTimeline";

export default Timeline;
